import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ArrowLeft, FileText, Link as LinkIcon, CheckCircle2, Clock, AlertTriangle, ListChecks, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StudentAssignments() {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);

    const refresh = async () => {
        const r = await api.get("/student/assignments");
        setItems(r.data);
    };
    useEffect(() => { refresh(); }, []);

    const fileHref = (file_id) => {
        const token = localStorage.getItem("token");
        return `${BACKEND}/api/lms/file/${file_id}?auth=${encodeURIComponent(token || "")}`;
    };

    const trackEvent = async (aid, kind, idx) => {
        try { await api.patch(`/student/assignment/${aid}/track`, { kind, resource_index: idx }); }
        catch (e) { /* fail silently — tracking is best-effort */ }
    };

    const openAssignment = async (a) => {
        setActive(a);
        if (!a.opened_at) {
            await trackEvent(a.id, "open");
            // Optimistic update
            setItems((it) => it.map((x) => (x.id === a.id ? { ...x, opened_at: new Date().toISOString(), status: "opened" } : x)));
        }
    };

    const handleResourceClick = (a, r, i) => {
        const kind = r.type === "quiz" ? "quiz_click" : r.type === "file" ? "file_click" : "link_click";
        trackEvent(a.id, kind, i);
    };

    const statusChip = (a) => {
        if (a.status === "opened") return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sage/10 text-sage border border-sage/30">Hapur</span>;
        if (a.is_expired) return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-terracotta/10 text-terracotta border border-terracotta/30">Skadoi</span>;
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-300">Pritur</span>;
    };

    const pending = items.filter((a) => !a.opened_at && !a.is_expired);

    return (
        <Layout>
            <Link to="/student" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Detyrat e mia</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Detyrat individuale</h1>
                <p className="text-slate-600 mt-2 text-sm">Hap detyrën, lexo materialin, dhe bëj quizin për të ndaluar njoftimet ditore.</p>
            </div>

            {pending.length > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-5 flex items-center gap-3" data-testid="student-pending-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
                    <div className="flex-1">
                        <div className="font-bold text-amber-900 text-sm">Ke {pending.length} detyrë{pending.length > 1 ? " të hapura për lexim" : " të hapur për lexim"}</div>
                        <div className="text-[11px] text-amber-700 mt-0.5">Hapi sa më parë për të ndaluar kujtesat ditore.</div>
                    </div>
                </div>
            )}

            {items.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <ListChecks className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">Nuk ke detyra aktive</div>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="divide-y divide-slate-200">
                        {items.map((a) => (
                            <button key={a.id} onClick={() => openAssignment(a)} className="w-full text-left px-4 sm:px-5 py-3 hover:bg-bone/40 transition" data-testid={`sa-row-${a.id}`}>
                                <div className="flex items-center gap-3">
                                    <div className={`w-1.5 self-stretch rounded-full ${a.is_expired ? "bg-terracotta" : a.status === "opened" ? "bg-sage" : "bg-amber-500"}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-slate-900 text-sm flex items-center gap-2 flex-wrap">
                                            {a.title} {statusChip(a)}
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            {a.subject_name && `${a.subject_name} • `}
                                            Nga {a.teacher_name}
                                            {!a.is_expired && a.status !== "opened" && ` • ${a.days_remaining} ditë`}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Assignment detail modal */}
            {active && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setActive(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto my-8" data-testid="sa-detail">
                        <div className="px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
                            <div className="flex items-center gap-2 mb-1.5">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                    {active.is_expired ? "Skaduar" : `${active.days_remaining} ditë`} • {active.teacher_name}
                                </span>
                                {statusChip(active)}
                            </div>
                            <h3 className="font-heading text-2xl font-black text-slate-900 leading-tight">{active.title}</h3>
                            {active.subject_name && <div className="text-xs text-slate-500 mt-1">{active.subject_name}</div>}
                        </div>
                        <div className="p-5 space-y-4">
                            {active.instructions && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Udhëzime</div>
                                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{active.instructions}</p>
                                </div>
                            )}
                            {active.text_body && (
                                <div className="bg-bone/40 border border-slate-200 rounded-md p-4">
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Materiali për lexim</div>
                                    <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{active.text_body}</p>
                                </div>
                            )}
                            {(active.resources || []).length > 0 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Resurse</div>
                                    <div className="space-y-2">
                                        {active.resources.map((r, i) => (
                                            <a
                                                key={i}
                                                href={r.type === "file" ? fileHref(r.file_id) : r.url}
                                                target="_blank" rel="noopener noreferrer"
                                                onClick={() => handleResourceClick(active, r, i)}
                                                className={`flex items-center gap-3 p-3 rounded-md border-2 transition ${r.type === "quiz" ? "border-amber-300 bg-amber-50 hover:bg-amber-100" : "border-slate-200 bg-bone/40 hover:bg-bonehover"}`}
                                                data-testid={`sa-resource-${i}`}
                                            >
                                                {r.type === "file" ? <FileText className="w-5 h-5 text-sage shrink-0" /> : r.type === "quiz" ? <CheckCircle2 className="w-5 h-5 text-amber-700 shrink-0" /> : <LinkIcon className="w-5 h-5 text-oxford shrink-0" />}
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-bold text-slate-900 text-sm truncate">{r.label || r.filename || r.url}</div>
                                                    <div className="text-[11px] text-slate-500 truncate">
                                                        {r.type === "quiz" ? "Klik për të hapur quizin" : r.type === "file" ? `${r.filename || "Skedar"}` : r.url}
                                                    </div>
                                                </div>
                                                <ExternalLink className="w-4 h-4 text-slate-400" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <button onClick={() => setActive(null)} className="w-full px-4 py-2 rounded-md bg-oxford hover:bg-oxfordhover text-white font-bold text-sm" data-testid="sa-close">
                                Mbyll
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
